/**
 * Reading Meta's Ad Library from a PASTE (Lee, 2 Oct 2026).
 *
 * Our server cannot read the Ad Library (Meta answers a non-browser request with 403), so an
 * admin opens it, selects everything, copies, and pastes into Tools. The copy carries every
 * ad's Library ID, run dates, wording and picture address; these functions read them out and
 * line each ad up with the campaign it belongs to.
 *
 * Pure: no network, no clock. Written against a real copy of the page
 * (__tests__/fixtures/adLibraryPaste.html / .txt, taken 2 Oct 2026).
 *
 * ⚠️ This depends on how Meta lays the page out. It reads what a person can see ("Library ID:
 * 123", "7 Jan 2026 - 26 Jan 2026", the text under "Sponsored"), not class names, so a
 * restyle should not break it — but if it stops finding ads, the page says so and each ad
 * can still be done by hand (Campaign names, Paste picture, Details).
 */

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const ID_LINE = /^Library ID: (\d{10,20})$/;
// "7 Jan 2026" (UK) or "Jan 7, 2026" (US), depending on the viewer's Facebook language.
const DAY = '(\\d{1,2} [A-Za-z]{3,4} \\d{4}|[A-Za-z]{3,4} \\d{1,2}, \\d{4})';
const RANGE = new RegExp(`^${DAY} - ${DAY}`);
const STARTED = new RegExp(`^Started running on ${DAY}`);

const pad = (n) => String(n).padStart(2, '0');

/** "7 Jan 2026" or "Jan 7, 2026" → "2026-01-07"; null when it is not a date. */
export function isoDay(s) {
  const t = String(s || '').trim();
  let m = /^(\d{1,2}) ([A-Za-z]{3,4}) (\d{4})$/.exec(t);
  let d; let mon; let y;
  if (m) { [, d, mon, y] = m; } else {
    m = /^([A-Za-z]{3,4}) (\d{1,2}), (\d{4})$/.exec(t);
    if (!m) return null;
    [, mon, d, y] = m;
  }
  const month = MONTHS[mon.toLowerCase()];
  if (!month || +d < 1 || +d > 31) return null;
  return `${y}-${pad(month)}-${pad(+d)}`;
}

function datesFrom(line) {
  const range = RANGE.exec(line);
  if (range) return { startDay: isoDay(range[1]), endDay: isoDay(range[2]), active: false };
  const started = STARTED.exec(line);
  if (started) return { startDay: isoDay(started[1]), endDay: null, active: true };
  return null;
}

/** Only Meta's own image host, over https — this address is fetched and shown. */
export function isMetaImageUrl(url) {
  try {
    const u = new URL(String(url));
    return u.protocol === 'https:' && /(^|\.)fbcdn\.net$/.test(u.hostname);
  } catch (e) {
    return false;
  }
}

const tidyText = (s) => String(s || '')
  .replace(/​/g, '')
  .replace(/\n(0:00 \/ \d+:\d+|Low impression count)\s*$/g, '')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

// ── From the copy's HTML (carries the picture addresses) ─────────────────────────────

const ownText = (el) => [...el.childNodes].filter((n) => n.nodeType === 3).map((n) => n.nodeValue).join('').trim();

/** An element's wording with its line breaks kept: <br> is a new line, a link is its text. */
function textWithBreaks(el) {
  let out = '';
  el.childNodes.forEach((n) => {
    if (n.nodeType === 3) out += n.nodeValue;
    else if (n.nodeName === 'BR') out += '\n';
    else out += n.textContent;
  });
  return out;
}

function fromHtml(html) {
  if (typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  const ads = [];
  const labels = [...doc.querySelectorAll('span, div')].filter((e) => !e.children.length && ID_LINE.test(e.textContent.trim()));
  for (const label of labels) {
    const libraryId = ID_LINE.exec(label.textContent.trim())[1];
    // The card is the largest block around the label that holds only this one Library ID.
    let card = label;
    while (card.parentElement && (card.parentElement.textContent.match(/Library ID: \d/g) || []).length === 1) card = card.parentElement;

    const leaves = [...card.querySelectorAll('span, div')].filter((e) => !e.children.length);
    let dates = null;
    for (const e of leaves) { dates = datesFrom(e.textContent.trim()); if (dates) break; }
    if (!dates || !dates.startDay) continue; // not an ad card we understand

    // The wording: the first block after "Sponsored" that holds text of its own.
    const all = [...card.querySelectorAll('*')];
    const sponsored = all.findIndex((e) => e.nodeName === 'STRONG' && e.textContent.trim() === 'Sponsored');
    let text = '';
    if (sponsored >= 0) {
      const body = all.slice(sponsored + 1).find((e) => e.nodeName === 'SPAN' && ownText(e).length > 0
        && !/^(Low impression count|0:00|\/|\d+:\d+)$/.test(e.textContent.trim()));
      if (body) text = tidyText(textWithBreaks(body));
    }

    // The picture: the ad's own image, never the 60px page logo; a video gives its first frame.
    const pics = [...card.querySelectorAll('img')].map((i) => i.getAttribute('src')).filter(isMetaImageUrl);
    const video = card.querySelector('video[poster]');
    const poster = video && isMetaImageUrl(video.getAttribute('poster')) ? video.getAttribute('poster') : null;
    const creative = pics.filter((s) => !/[_=]s60x60[_&]/.test(s));
    const imageUrl = creative[0] || poster || null;

    const grouped = /(\d+) ads? use this creative and text/.exec(card.textContent);
    ads.push({
      libraryId, ...dates, text, imageUrl,
      picture: creative[0] ? 'image' : poster ? 'video' : 'none',
      grouped: grouped ? +grouped[1] : 1,
    });
  }
  return ads;
}

// ── From the copy's plain text (no pictures) ─────────────────────────────────────────

function fromText(text) {
  const lines = String(text).replace(/\r/g, '').split('\n').map((l) => l.replace(/​/g, '').trimEnd());
  const starts = lines.map((l, i) => (ID_LINE.test(l.trim()) ? i : -1)).filter((i) => i >= 0);
  return starts.map((at, n) => {
    const end = n + 1 < starts.length ? starts[n + 1] : lines.length;
    const block = lines.slice(at, end);
    const libraryId = ID_LINE.exec(block[0].trim())[1];
    let dates = null;
    for (const l of block.slice(1, 6)) { dates = datesFrom(l.trim()); if (dates) break; }
    if (!dates || !dates.startDay) return null;
    const sponsored = block.findIndex((l) => l.trim() === 'Sponsored');
    let body = [];
    if (sponsored >= 0) {
      for (const l of block.slice(sponsored + 1)) {
        // The link's domain in capitals ("CLOZESURE.COM"), or the next card's status, ends the wording.
        if (/^[A-Z0-9.-]+\.[A-Z]{2,}$/.test(l.trim()) || /^(Active|Inactive)$/.test(l.trim())) break;
        body.push(l);
      }
    }
    const grouped = block.map((l) => /^(\d+) ads? use this creative and text$/.exec(l.trim())).find(Boolean);
    return { libraryId, ...dates, text: tidyText(body.join('\n')), imageUrl: null, picture: 'none', grouped: grouped ? +grouped[1] : 1 };
  }).filter(Boolean);
}

/**
 * Every ad in what was pasted.
 * @param {{html?: string, text?: string}} paste  the clipboard's two flavours
 * @returns [{ libraryId, startDay, endDay, active, text, imageUrl, picture, grouped }]
 */
export function parseAdLibraryPaste({ html, text } = {}) {
  const rich = html ? fromHtml(html) : [];
  const plain = text ? fromText(text) : [];
  if (!rich.length) return plain;
  // The HTML has the pictures; the plain text is the safer source of wording if the HTML's is empty.
  const byId = new Map(plain.map((a) => [a.libraryId, a]));
  return rich.map((a) => (a.text ? a : { ...a, text: (byId.get(a.libraryId) || {}).text || '' }));
}

// ── Lining ads up with campaigns ─────────────────────────────────────────────────────

const dayNumber = (iso) => Math.round(Date.parse(`${iso}T12:00:00Z`) / 86400000);

/**
 * Which pasted ad belongs to which campaign. The ad for a campaign is the one that STARTED on
 * the day of the campaign's first click (all 18 existing ads matched that way), or the day
 * before when the first click came after midnight.
 *
 * Only a single clear candidate is matched; anything else is left for a person to choose.
 * An ad already linked to another campaign, or already taken in this pass, is not offered.
 *
 * @param campaigns [{ key, firstDay }]   firstDay: 'YYYY-MM-DD' of the first click, or null
 * @param ads       parseAdLibraryPaste() output
 * @param usedLibraryIds  Library IDs other campaigns already carry
 * @returns { [key]: { libraryId: string|null, how: 'same-day'|'day-before'|'none'|'several'|'unknown-start' } }
 */
export function matchAdsToCampaigns(campaigns, ads, usedLibraryIds = []) {
  const taken = new Set(usedLibraryIds);
  const out = {};
  const free = () => ads.filter((a) => !taken.has(a.libraryId));
  const pass = (how, gap) => {
    for (const c of campaigns) {
      if (out[c.key] && out[c.key].libraryId) continue;
      if (!c.firstDay) { out[c.key] = { libraryId: null, how: 'unknown-start' }; continue; }
      const hits = free().filter((a) => dayNumber(c.firstDay) - dayNumber(a.startDay) === gap);
      if (hits.length === 1) { out[c.key] = { libraryId: hits[0].libraryId, how }; taken.add(hits[0].libraryId); }
      else if (hits.length > 1) out[c.key] = { libraryId: null, how: 'several' };
      else if (!out[c.key]) out[c.key] = { libraryId: null, how: 'none' };
    }
  };
  pass('same-day', 0);
  pass('day-before', 1);
  return out;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A starting name for an ad: its opening words and the month it started, e.g.
 * "Found a new home you love? (Sep 2026)". The month keeps a second run of the same ad
 * distinct — two campaigns may not share a name. The admin can change it before saving.
 */
export function suggestAdName(text, startDay) {
  const first = String(text || '').split('\n').map((l) => l.trim()).find(Boolean) || '';
  // Drop leading emoji and symbols; keep letters, digits and ordinary punctuation.
  let s = first.replace(/^[^A-Za-z0-9"'(]+/, '').replace(/\s+/g, ' ').trim();
  const sentence = /^(.{8,60}?[.!?…])(\s|$)/.exec(s);
  if (sentence) s = sentence[1].replace(/[.…]+$/, '');
  if (s.length > 52) s = s.slice(0, 52).replace(/\s+\S*$/, '');
  const when = /^(\d{4})-(\d{2})/.exec(startDay || '');
  const month = when ? ` (${MONTH_NAMES[+when[2] - 1]} ${when[1]})` : '';
  return (s || 'Meta ad') + month;
}
